import PaperButton from "components/buttons/PaperButton";
import CustomLink from "components/CustomLink";
import CustomTable from "components/CustomTable";
import ExportReactCSV from "components/ExportReactCSV";
import { CustomStatusBadge } from "components/StatusBadge";
import CustomText from "components/typography/CustomText";
import { formatArray } from "lib/arrayObject";
import { getWeekDate, getWeekDaysByWeekNumber } from "lib/date";
import { currency } from "lib/formatter";
import { boxStatusOptions, DELIVERY_FEE } from "lib/global";
import { toNumber } from "lodash";
import moment from "moment";

export default function SubscriptionsTable({
  data = [],
  onLoadData = () => {},
  totalPage = 1,
  page = 1,
}) {
  const headers = [
    { label: "Sub ID", key: "id" },
    { label: "Created", key: "created" },
    { label: "Customer", key: "name" },
    { label: "Value", key: "amount" },
    { label: "Frequency", key: "frequency" },
    { label: "Dispatch Date", key: "next_dispatch" },
    // { label: "Last Dispatch", key: "last_dispatch" },
    { label: "Address", key: "address" },
    { label: "status", key: "status" },
  ];

  const subscriptions = () => {
    let custs = data.map((row) => ({
      id: row?.id ?? "",
      created: moment(row?.created_at).format("DD/MM/YY") ?? "",
      name: row?.name ?? "",
      amount:
        currency.format(
          formatArray(row?.pies).reduce(
            (ret, cur) => ret + toNumber(cur?.price ?? 0),
            0
          ) + DELIVERY_FEE
        ) ?? "",
      frequency: `${row?.lastWeeklySlot?.frequency} weeks`,
      next_dispatch: getWeekDaysByWeekNumber({
        weekNumber:
          row?.lastWeeklySlot?.week_next_num ??
          row?.lastWeeklySlot?.week_num ??
          1,
        addDay: row?.lastWeeklySlot?.week_day - 1,
        format: "DD/MM/YY",
        yearNumber: row?.lastWeeklySlot?.week_year,
      }),
      last_dispatch: `${
        row?.lastWeeklySlot?.week_next_num ||
        (row?.lastWeeklySlot?.frequency + row?.lastWeeklySlot?.week_num ?? 0)
      } weeks`,
      address: row?.address?.label ?? "",
      status:
        boxStatusOptions?.find((item) => item?.value === (row?.status || "1"))
          ?.label ?? "",
    }));
    return custs;
  };

  const handleChangePage = (p, sortKey) => {
    onLoadData({ page: p, sortKey });
  };

  const handleSortKey = (sortKey) => {
    onLoadData({ sortKey, page });
  };

  return (
    <div className="pb-8">
      <CustomTable
        data={data}
        height={1000}
        isPageActive={true}
        page={page}
        totalPage={totalPage}
        onChangePage={handleChangePage}
        onSortKey={handleSortKey}
        toolbar={
          <div className="p-4 border-b border-white-gray flex justify-between items-center">
            <CustomText variant="h6" bold={600}>
              Subscriptions
            </CustomText>
            <ExportReactCSV
              csvHeaders={headers}
              csvData={subscriptions()}
              fileName={`subscriptions.csv`}
            />
          </div>
        }
        itemRoute="subscription"
        itemIndex="id"
        columns={[
          {
            label: "SUB ID",
            selector: "id",
            cell: (row) => (
              <CustomText
                className="text-13"
                variant="text"
                color="primary"
                bold={600}
              >
                <CustomLink to={`/subscriptions/${row.id}`}>
                  {row?.id}
                </CustomLink>
              </CustomText>
            ),
          },
          {
            label: "CREATED",
            selector: "created_at",
            sortValue: (row) => moment(row?.created_at).unix(),
            cell: (row) => (
              <CustomText className="text-13">
                {moment(row?.created_at).format("DD/MM/YY")}
              </CustomText>
            ),
          },
          {
            label: "CUSTOMER",
            selector: "name",
          },
          {
            label: "VALUE",
            selector: "amount",
            sortValue: (row) =>
              formatArray(row?.pies).reduce(
                (ret, cur) => ret + toNumber(cur?.price ?? 0),
                0
              ),
            cell: (row) => (
              <CustomText className="text-13">
                {currency.format(
                  formatArray(row?.pies).reduce(
                    (ret, cur) => ret + toNumber(cur?.price ?? 0),
                    0
                  ) + DELIVERY_FEE
                )}
              </CustomText>
            ),
          },
          {
            label: "FREQUENCY",
            selector: "frequency",
            sortValue: (row) => row?.lastWeeklySlot?.frequency,
            cell: (row) => (
              <CustomText className="text-13">
                {`${row?.lastWeeklySlot?.frequency} weeks`}
              </CustomText>
            ),
          },
          {
            label: "DISPATCH DATE",
            selector: "fulfillment",
            sortValue: (row) =>
              getWeekDate({
                weekNumber:
                  row?.lastWeeklySlot?.week_next_num ??
                  row?.lastWeeklySlot?.week_num ??
                  1,
                addDay: row?.lastWeeklySlot?.week_day - 1,
                yearNumber: row?.lastWeeklySlot?.week_year,
              }).unix(),
            cell: (row) => (
              <CustomText className="text-13">
                {getWeekDaysByWeekNumber({
                  weekNumber:
                    row?.lastWeeklySlot?.week_next_num ??
                    row?.lastWeeklySlot?.week_num ??
                    1,
                  addDay: row?.lastWeeklySlot?.week_day - 1,
                  format: "DD/MM/YY",
                  yearNumber: row?.lastWeeklySlot?.week_year,
                })}
              </CustomText>
            ),
          },
          {
            label: "ADDRESS",
            selector: "address",
            sortValue: (row) => row?.address?.label,
            cell: (row) => (
              <CustomText
                className="text-13"
                variant="text"
                color="primary"
                bold={600}
              >
                {row?.address?.label ?? ""}
              </CustomText>
            ),
          },
          {
            label: "STATUS",
            selector: "status",
            sortValue: (row) =>
              boxStatusOptions?.find(
                (item) => item?.value === (row?.status || "1")
              )?.label ?? "",
            cell: (row) => (
              <div className="flex items-center">
                <CustomStatusBadge
                  size="body"
                  variant={
                    boxStatusOptions?.find(
                      (item) => item?.value === (row?.status || "1")
                    )?.variant
                  }
                  text={
                    boxStatusOptions?.find(
                      (item) => item?.value === (row?.status || "1")
                    )?.label ?? ""
                  }
                />
              </div>
            ),
          },
          {
            label: "",
            selector: "actions",
            cell: (row, rowIndex) => (
              <CustomLink
                to={`/subscriptions/${row.id}`}
                variant={"text"}
                color={"primary"}
              >
                <PaperButton
                  // onClick={() => onView(row, rowIndex)}
                  variant="text"
                  className="text-13"
                  color="primary"
                >
                  View
                </PaperButton>
              </CustomLink>
            ),
          },
        ]}
      />
    </div>
  );
}
